<template>
  <Transition name="fade">
    <Dialog
      v-if="isOpen"
      title="Copy Order"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      md
      @closeEvent="closeModal"
      @cancelEvent="closeModal"
      @saveEvent="submitCopyOrder"
      :saveButtonProps="{ disabled: false }"
      :saveButtonDisabled="isSubmitting"
    >
      <template #modal-content>
        <div class="flex flex-col gap-3">
          <!-- Header -->
          <div class="flex items-center justify-between border-b pb-2 mb-2">
            <h2 class="text-lg font-semibold">Select Products to Copy</h2>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="selectAllItems" @change="toggleSelectAllItems" />
              <span class="text-sm font-medium">Select All Items</span>
            </label>
          </div>

          <!-- Items -->
          <div v-for="item in order.items" :key="item.id" class="w-full">
            <Accordion :default-open="true">
              <template #header>
                <div class="flex items-center gap-3 w-full">
                  <input
                    type="checkbox"
                    v-model="itemSelections[item.id].selectAll"
                    class="w-4 h-4 text-primary"
                    @change="toggleItemSelectAll(item)"
                    @click="(e) => e.stopPropagation()"
                  />
                  <span class="font-medium">Item #{{ item.id }}</span>
                </div>
              </template>

              <template #content>
                <div class="grid grid-cols-3 gap-4 mt-2">
                  <div
                    v-for="fp in item.factory_products"
                    :key="fp.id"
                    @click="toggleProductSelection(item.id, fp.id)"
                    class="relative cursor-pointer border rounded-lg p-2 flex flex-col items-center transition-all duration-150"
                    :class="{
                      'border-primary bg-primary/10': itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                      'border-gray-200 hover:border-primary/50': !itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                    }"
                  >
                    <!-- Checkbox (optional small corner one) -->
                    <input
                      type="checkbox"
                      :checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                      class="absolute top-2 left-2 w-4 h-4 text-primary pointer-events-none"
                    />

                    <!-- Product Info -->
                    <div class="grid grid-cols-2 gap-2 w-full">
                      <div class="flex flex-col items-center">
                        <img
                          :src="fp.front_image ? storage_url + '/' + fp.front_image: placeholderImg"
                          alt="front"
                          class="size-[100px] object-cover rounded-md border"
                        />
                        <span class="text-xs mt-1 text-gray-600">Front</span>
                      </div>
                      <div class="flex flex-col items-center">
                        <img
                          :src="fp.back_image ? storage_url + '/' + fp.back_image: placeholderImg"
                          alt="back"
                          class="size-[100px] object-cover rounded-md border"
                        />
                        <span class="text-xs mt-1 text-gray-600">Back</span>
                      </div>
                    </div>

                    <p class="text-sm font-medium mt-2 text-center">
                      {{
                        (fp.new_product_name ? fp.new_product_name : fp.product_name) ||
                        `Product #${fp.id}`
                      }}
                    </p>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>

<script setup lang="ts">
import placeholderImg from '@/assets/img/placeholder.png';
import Accordion from '@/components/Shared/Accordion.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { OrderDetail, OrderDetailItem } from '@/models';
import { reactive, ref } from 'vue';

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const props = defineProps<{ order: OrderDetail }>();
const isSubmitting = ref(false);

const isOpen = ref(true);
const selectAllItems = ref(false);
const itemSelections = reactive<
  Record<string, { selectAll: boolean; selectedProducts: (string | number)[] }>
>({});
const emit = defineEmits(['close', 'save']);

// ✅ Initialize selections before render
if (props.order?.items?.length) {
  props.order.items.forEach((item) => {
    itemSelections[item.id] = {
      selectAll: false,
      selectedProducts: [],
    };
  });
}

/* -------------------------------------------------------------------------- */
/* ✅ FIX: Explicit "Select All Items" toggle instead of watcher               */
/* -------------------------------------------------------------------------- */
const toggleSelectAllItems = () => {
  const newVal = selectAllItems.value;
  props.order.items.forEach((item) => {
    const itemSel = itemSelections[item.id];
    if (!itemSel) return;
    itemSel.selectAll = newVal;
    itemSel.selectedProducts = newVal ? item.factory_products.map((fp) => fp.id) : [];
  });
};

/* -------------------------------------------------------------------------- */
const toggleItemSelectAll = (item: OrderDetailItem) => {
  const itemSel = itemSelections[item.id];
  if (!itemSel) return;
  itemSel.selectedProducts = itemSel.selectAll ? item.factory_products.map((fp) => fp.id) : [];

  // 🧠 Sync global "Select All Items" if needed
  updateGlobalSelectAll();
};

const toggleProductSelection = (itemId: string | number, productId: string | number) => {
  const itemSel = itemSelections[itemId];
  if (!itemSel) return;
  const index = itemSel.selectedProducts.indexOf(productId);
  if (index === -1) {
    itemSel.selectedProducts.push(productId);
  } else {
    itemSel.selectedProducts.splice(index, 1);
  }

  // auto toggle "select all" for that item
  const total = props.order.items.find((i) => i.id === itemId)?.factory_products.length || 0;
  itemSel.selectAll = itemSel.selectedProducts.length === total;

  // 🧠 Sync global "Select All Items"
  updateGlobalSelectAll();
};

// 🧩 Keep top-level selectAllItems reactive (without triggering resets)
const updateGlobalSelectAll = () => {
  const allSelected = props.order.items.every((item) => itemSelections[item.id]?.selectAll);
  selectAllItems.value = allSelected;
};

/* -------------------------------------------------------------------------- */
const buildPayload = () => {
  if (selectAllItems.value) return {};
  const map: Record<string, (string | number)[]> = {};
  for (const item of props.order.items) {
    const selected = itemSelections[item.id]?.selectedProducts ?? [];
    if (selected.length > 0) {
      map[item.id] = selected;
    }
  }
  return { order_item_factory_products_map: map };
};

const submitCopyOrder = async () => {
  isSubmitting.value = true;
  const payload = buildPayload();
  try {
    const resp = await http.post(
      `/copy-order/${props.order.id}`,
      Object.keys(payload).length ? payload : {}
    );
    showToast(resp.data.message || 'Order copied successfully!', 'success');
    emit('save', true)
    closeModal();
  } catch (err: any) {
    handleResponseException(err);
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
